import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";
import { Reveal, HeroVideo } from "../components/motion";
import { PageHero, CtaBand, FaqList } from "../components/shared";
import { QuoteLink } from "../components/QuotePanel";
import { imgProps } from "../components/media";
import { AREAS, AIRPORTS, VISITS, OBIETTIVI_BENTO, FAQS, img, dim } from "../data/site";

export default function ObiettiviSensibili() {
  const [area, setArea] = useState(0);
  const reduce = useReducedMotion();
  const a = AREAS[area];
  const isIll = a.img.endsWith(".svg");
  return (
    <>
      <PageHero
        kick="Obiettivi sensibili"
        lines={["Dove la sicurezza", "non ammette errori."]}
        sub="Aeroporti, porti, stazioni, ambasciate e infrastrutture strategiche: i siti che il D.M. 269/2010 affida esclusivamente alle guardie particolari giurate."
        ph={img("controllo-merci-aeroporto.jpg")}
        video={{ src: "/assets/video/loop-aeroporto-1080.mp4", poster: "/assets/video/loop-aeroporto-poster.jpg" }}
        ctas={
          <QuoteLink servizio="obiettivi-sensibili" className="btn" testid="os-hero-cta">
            Richiedi un incontro
          </QuoteLink>
        }
        trust={AIRPORTS.map(([c]) => (
          <span key={c} style={{ fontFamily: "var(--arch)", fontWeight: 800, letterSpacing: ".08em" }}>
            {c}
          </span>
        ))}
      />

      <section className="sec" data-testid="os-ambiti">
        <div className="wrap">
          <Reveal className="sec-head">
            <p className="kick">Tre ambiti</p>
            <h2 className="h2">Protocolli diversi, stesso rigore</h2>
          </Reveal>
          <Reveal className="tabs">
            {AREAS.map((ar, i) => (
              <button key={ar.label} type="button" className={`tab ${area === i ? "on" : ""}`} onClick={() => setArea(i)} data-testid={`os-tab-${i}`}>
                {ar.label}
              </button>
            ))}
          </Reveal>
          <AnimatePresence mode="wait">
            <motion.div key={a.label} initial={reduce ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={reduce ? { opacity: 0 } : { opacity: 0, y: -16 }} transition={{ duration: reduce ? 0 : 0.4, ease: [0.2, 0.7, 0.2, 1] }}>
              <div className="svc-row">
                <div className={isIll ? "illpanel" : "phframe tall"}>
                  <img {...imgProps(a.img, { sizes: "(max-width: 760px) 92vw, 560px" })} alt={a.title} />
                </div>
                <div className="svc-info">
                  <span className="svc-kind">{a.norm}</span>
                  <h3 className="h3">{a.title}</h3>
                  <p style={{ color: "var(--muted)", margin: 0 }}>{a.text}</p>
                  <ul className="includes">
                    {a.inc.map((x) => (
                      <li key={x}>
                        <Check size={17} className="ck" aria-hidden="true" /> {x}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      <section className="sec-blue sec" data-testid="os-visite">
        <div className="wrap">
          <Reveal className="sec-head">
            <p className="kick">Missioni di alto profilo</p>
            <h2 className="h2">{VISITS.title}</h2>
            <p style={{ margin: "24px 0 0", color: "#D9DEE6", maxWidth: 880, fontSize: 19 }}>{VISITS.text}</p>
          </Reveal>
          <div className="minis">
            {VISITS.cards.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.07}>
                <div className="mini" style={{ background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.16)" }} data-testid={`os-visit-${i}`}>
                  <span style={{ display: "block", width: 34, height: 3, background: "var(--gold)", marginBottom: 18 }} aria-hidden="true" />
                  <h3 style={{ color: "#fff" }}>{t}</h3>
                  <p style={{ color: "#D9DEE6" }}>{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="sec" data-testid="os-dm">
        <div className="wrap">
          <Reveal className="sec-head">
            <p className="kick">D.M. 269/2010</p>
            <h2 className="h2">Quali sono gli obiettivi sensibili</h2>
          </Reveal>
          <div className="bento">
            {OBIETTIVI_BENTO.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.05}>
                <div className="cell" data-testid={`os-bento-${i}`}>
                  <h3>{t}</h3>
                  <p>{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="sec" style={{ paddingTop: 0 }} data-testid="os-faq">
        <div className="wrap" style={{ display: "flex", flexWrap: "wrap", gap: "40px 90px" }}>
          <Reveal style={{ flex: "1 1 360px" }}>
            <p className="kick">Domande frequenti</p>
            <h2 className="h2">Domande sugli obiettivi sensibili</h2>
          </Reveal>
          <Reveal delay={0.1} style={{ flex: "2 1 560px", minWidth: 0 }}>
            <FaqList items={FAQS.obiettivi} testid="os-faq" />
          </Reveal>
        </div>
      </section>

      <CtaBand testid="os-cta" />
    </>
  );
}
